/**
 * React Query Client Configuration
 * Centralized query client with optimized defaults
 */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

// Custom error handler
const handleError = (error: unknown) => {
  const message = error instanceof Error ? error.message : 'An error occurred';
  console.error('Query/Mutation Error:', message);
  // Toast notifications can be integrated here
};

// Create query client with optimized defaults
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only show error toasts for queries that have already been successful once
      // This prevents showing errors for initial loads
      if (query.state.data !== undefined) {
        handleError(error);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: handleError,
  }),
  defaultOptions: {
    queries: {
      // Data is fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests 2 times
      retry: 2,
      // Don't refetch on window focus in development
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      // Don't refetch on reconnect automatically
      refetchOnReconnect: 'always',
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

// Query key factory for consistent key management
export const queryKeys = {
  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  
  // Companies
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
    subsidiaries: (id: string) => [...queryKeys.companies.detail(id), 'subsidiaries'] as const,
  },
  
  // Bank Accounts
  accounts: {
    all: ['accounts'] as const,
    lists: () => [...queryKeys.accounts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.accounts.lists(), filters] as const,
    details: () => [...queryKeys.accounts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.accounts.details(), id] as const,
    byCompany: (companyId: string) => [...queryKeys.accounts.all, 'company', companyId] as const,
  },
  
  // Transactions
  transactions: {
    all: ['transactions'] as const,
    lists: () => [...queryKeys.transactions.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.transactions.lists(), filters] as const,
    details: () => [...queryKeys.transactions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.transactions.details(), id] as const,
    byAccount: (accountId: string) => [...queryKeys.transactions.all, 'account', accountId] as const,
    summary: (filters?: Record<string, unknown>) => [...queryKeys.transactions.all, 'summary', filters] as const,
  },
  
  // Statements
  statements: {
    all: ['statements'] as const,
    lists: () => [...queryKeys.statements.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.statements.lists(), filters] as const,
    details: () => [...queryKeys.statements.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.statements.details(), id] as const,
    byAccount: (accountId: string) => [...queryKeys.statements.all, 'account', accountId] as const,
  },
  
  // Mutual Funds
  mutualFunds: {
    all: ['mutualFunds'] as const,
    lists: () => [...queryKeys.mutualFunds.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.mutualFunds.lists(), filters] as const,
    details: () => [...queryKeys.mutualFunds.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.mutualFunds.details(), id] as const,
    byCompany: (companyId: string) => [...queryKeys.mutualFunds.all, 'company', companyId] as const,
    summary: () => [...queryKeys.mutualFunds.all, 'summary'] as const,
  },
  
  // Auth
  auth: {
    user: ['auth', 'user'] as const,
    session: ['auth', 'session'] as const,
  },
  
  // Dashboard/Analytics
  dashboard: {
    stats: ['dashboard', 'stats'] as const,
    charts: (type: string) => ['dashboard', 'charts', type] as const,
  },
} as const;

// Helper to invalidate all queries for an entity
type EntityWithAll = 'users' | 'companies' | 'accounts' | 'transactions' | 'statements' | 'mutualFunds';
export const invalidateEntity = (entity: EntityWithAll) => {
  return queryClient.invalidateQueries({ queryKey: queryKeys[entity].all });
};

// Helper to prefetch entity data
export const prefetchEntity = async <T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>
) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: 60 * 1000, // Consider fresh for 1 minute when prefetching
  });
};

export default queryClient;
